import { MOBILE_MAX, Text } from '@property.com.au/ui-kit';
import type { FunctionComponent } from 'react';
import { WebLink } from '../../../../app-kit/components/WebLink/WebLink';

import type { WebLink as IWebLink } from '../../../../app-kit/components/WebLink/types';
import {
  REALogo,
  REALogoContainer,
  HighlightedBoxTitle,
  HighlightedBox,
  InfoSection,
  StyledStack,
  Subtitle,
  Paragraph,
  UntitledList,
  ListItem,
  HelpOutboundLink,
} from '../UpdatePropertyDetailsModal.styled';
import { ModalDialog } from '../../../../components/ModalDialog/ModalDialog';
import REALogoSVG from '../../../../assets/rea-com-au-logo.svg';

interface UpdatePropertyDetailsModalContentProps {
  onClose: () => void;
  subtitle: string;
  link: IWebLink;
}

export const UpdatePropertyDetailsModalContent: FunctionComponent<
  UpdatePropertyDetailsModalContentProps
> = ({ subtitle, link, onClose }) => {
  return (
    <ModalDialog
      autoFullScreen
      fullScreenMediaQuery={`(max-width: ${MOBILE_MAX}px)`}
      onClose={onClose}
      header={
        <StyledStack gap="0.125rem">
          <Text as="h1" variant="subtitle01">
            Request update
          </Text>
          <Text as="h2" variant="body01">
            {subtitle}
          </Text>
        </StyledStack>
      }
      footer={
        <WebLink
          key={link.key}
          title={link.title}
          url={link.url}
          variant={link.variant}
          isExternal={link.isExternal}
          tracking={link.tracking}
          fullWidth
          onClick={onClose}
        />
      }
    >
      <HighlightedBox>
        <HighlightedBoxTitle>Submit a request with</HighlightedBoxTitle>
        <REALogoContainer>
          <REALogo src={REALogoSVG} alt="Realestate.com.au logo" />
        </REALogoContainer>
      </HighlightedBox>

      <InfoSection>
        <Subtitle>
          Primary property data shown on Property.com.au is sourced from Realestate.com.au
        </Subtitle>
        <Paragraph>
          Personal information will be required to submit the request through realestate.com.au’s
          support system for the purposes of identification.
        </Paragraph>
        <Paragraph>
          You are currently only able to request updates to the following property attributes:
        </Paragraph>
        <UntitledList>
          <ListItem>Bedrooms</ListItem>
          <ListItem>Bathrooms</ListItem>
          <ListItem>Car spaces</ListItem>
          <ListItem>Property Type</ListItem>
          <ListItem>Land Area</ListItem>
          <ListItem>Floor Area</ListItem>
          <ListItem>Year Built</ListItem>
        </UntitledList>
        <Paragraph>
          For all other enquiries, please refer to our{' '}
          <HelpOutboundLink
            href="https://help.property.com.au/"
            outboundLinkType="home-update-property-details-help"
          >
            help centre.
          </HelpOutboundLink>
        </Paragraph>
        <Paragraph>
          {' '}
          Some property updates will have a greater effect on new estimate value than others.
        </Paragraph>
        <Paragraph>
          Information supplied may take up to two weeks to be reflected across Property.com.au.
        </Paragraph>
      </InfoSection>
    </ModalDialog>
  );
};
